
import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { recruitmentApi } from '@/api/recruitment'
import { 
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  Users,
  Target,
  BookOpen,
  Zap,
  FileText,
  Save,
  Send,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Building,
  Calendar,
  Eye,
  Edit,
  Share
} from 'lucide-react'
import { usePageTitle } from '@/stores/page-title-store'

export default function NewRecruitmentPage() {
  const { user } = useAuth()
  usePageTitle('Nova Vaga')
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    // Job Basic Information
    basicInfo: {
      title: '',
      department: '',
      level: '',
      contractType: 'CLT' as 'CLT' | 'PJ',
      location: 'Brasília/DF',
      workMode: 'hybrid' as 'remote' | 'hybrid' | 'onsite',
      urgency: 'normal' as 'low' | 'normal' | 'high' | 'urgent'
    },
    // Compensation & Benefits
    compensation: {
      salaryMin: 0,
      salaryMax: 0,
      showSalary: false,
      benefits: [] as string[],
      workload: 40
    },
    // Job Description
    description: {
      summary: '',
      responsibilities: [] as string[],
      requirements: [] as string[],
      preferences: [] as string[],
      challenges: [] as string[]
    },
    // Company & Culture
    culture: {
      teamDescription: '',
      growthOpportunities: '',
      companyValues: [] as string[],
      workEnvironment: ''
    },
    // Recruitment Process
    process: {
      stages: [] as Array<{ name: string; description: string; duration: string }>,
      hiringManager: '',
      expectedStartDate: '',
      positionsAvailable: 1,
      applicationDeadline: ''
    }
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form dropdown options
  const departments = [
    'Estratégia',
    'Criação',
    'Produção',
    'Atendimento',
    'Administrativo',
    'Financeiro',
    'RH',
    'TI',
    'Digital'
  ]

  const levels = [
    'Estagiário',
    'Júnior',
    'Pleno',
    'Sênior',
    'Especialista',
    'Coordenador',
    'Gerente',
    'Diretor'
  ]

  const defaultBenefits = [
    'Plano de Saúde',
    'Plano Odontológico', 
    'Vale Refeição',
    'Vale Transporte',
    'Auxílio Academia',
    'Flexibilidade de Horário',
    'Home Office',
    'Licença Maternidade/Paternidade Estendida',
    'Plano de Carreira',
    'Treinamentos e Certificações',
    'Participação nos Lucros',
    'Desconto em Produtos/Serviços'
  ]

  const monumentaValues = [
    'Independência',
    'Inovação',
    'Colaboração',
    'Excelência',
    'Transparência',
    'Sustentabilidade',
    'Diversidade',
    'Desenvolvimento Contínuo'
  ]

  const defaultProcessStages = [
    { name: 'Análise de Currículo', description: 'Avaliação inicial de qualificações', duration: '3 dias' },
    { name: 'Entrevista com RH', description: 'Conversa sobre perfil e cultura', duration: '1 semana' },
    { name: 'Teste Técnico', description: 'Avaliação de competências técnicas', duration: '1 semana' },
    { name: 'Entrevista com Gestor', description: 'Conversa técnica e comportamental', duration: '1 semana' },
    { name: 'Proposta', description: 'Apresentação da oferta', duration: '3 dias' }
  ]

  const managers = [
    { id: 'manager-1', name: 'Patricia Lima', department: 'Estratégia' },
    { id: 'manager-2', name: 'Carlos Silva', department: 'Criação' },
    { id: 'manager-3', name: 'Maria Santos', department: 'Produção' },
    { id: 'manager-4', name: 'João Oliveira', department: 'Atendimento' }
  ]

  const steps = [
    { id: 1, title: 'Informações Básicas', icon: Briefcase },
    { id: 2, title: 'Remuneração', icon: DollarSign },
    { id: 3, title: 'Descrição da Vaga', icon: FileText },
    { id: 4, title: 'Cultura & Ambiente', icon: Users },
    { id: 5, title: 'Processo Seletivo', icon: Target },
    { id: 6, title: 'Revisão', icon: CheckCircle }
  ]

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    switch (step) {
      case 1:
        if (!formData.basicInfo.title) newErrors.title = 'Título da vaga é obrigatório'
        if (!formData.basicInfo.department) newErrors.department = 'Departamento é obrigatório'
        if (!formData.basicInfo.level) newErrors.level = 'Nível é obrigatório'
        break
      case 2:
        if (formData.compensation.salaryMin <= 0) newErrors.salaryMin = 'Salário mínimo deve ser maior que zero'
        if (formData.compensation.salaryMax < formData.compensation.salaryMin) {
          newErrors.salaryMax = 'Salário máximo deve ser maior que o mínimo'
        }
        break
      case 3:
        if (!formData.description.summary) newErrors.summary = 'Resumo da vaga é obrigatório'
        if (formData.description.responsibilities.length === 0) newErrors.responsibilities = 'Adicione pelo menos uma responsabilidade'
        if (formData.description.requirements.length === 0) newErrors.requirements = 'Adicione pelo menos um requisito'
        break
      case 5:
        if (!formData.process.hiringManager) newErrors.hiringManager = 'Gestor responsável é obrigatório'
        if (!formData.process.expectedStartDate) newErrors.expectedStartDate = 'Data de início é obrigatória'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length))
    }
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleInputChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [field]: value
      }
    }))
  }

  const addToArray = (section: string, field: string, value: string) => {
    if (!value.trim()) return
    
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [field]: [...(prev as any)[section][field], value.trim()]
      }
    }))
  }

  const removeFromArray = (section: string, field: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [field]: (prev as any)[section][field].filter((_: any, i: number) => i !== index)
      }
    }))
  }

  const handleSubmit = async () => {
    if (!validateStep(5)) return

    setIsSubmitting(true)
    try {
      await recruitmentApi.createJobPosting({
        title: formData.basicInfo.title,
        department: formData.basicInfo.department,
        description: formData.description.summary,
        location: formData.basicInfo.location,
        workMode: formData.basicInfo.workMode as any,
        employmentType: formData.basicInfo.contractType?.toLowerCase() as any,
        seniority: formData.basicInfo.level?.toLowerCase() as any,
        responsibilities: formData.description.responsibilities,
        requirements: {
          technical: formData.description.requirements,
          behavioral: formData.description.preferences
        },
        benefits: formData.compensation.benefits,
        salaryRange: formData.compensation.salaryMin && formData.compensation.salaryMax
          ? { min: Number(formData.compensation.salaryMin), max: Number(formData.compensation.salaryMax), currency: 'BRL' }
          : null,
        status: 'active',
        visibility: 'public'
      })

      alert('Vaga criada com sucesso!')
      window.location.href = '/recruitment'

    } catch (error) {
      console.error('Erro ao criar vaga:', error)
      alert('Erro ao criar vaga. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Título da Vaga */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Título da Vaga *
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={formData.basicInfo.title}
                    onChange={(e) => handleInputChange('basicInfo', 'title', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none ${errors.title ? 'border-brand-red' : 'border-gray-700'}`}
                    placeholder="Ex: Designer Sênior, Desenvolvedor Full Stack, Account Manager"
                  />
                  {errors.title && <p className="text-brand-red text-sm mt-1">{errors.title}</p>}
                </div>
              </div>

              {/* Departamento */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Departamento *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <select
                    value={formData.basicInfo.department}
                    onChange={(e) => handleInputChange('basicInfo', 'department', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white focus:border-brand-primary focus:outline-none ${errors.department ? 'border-brand-red' : 'border-gray-700'}`}
                  >
                    <option value="">Selecione o departamento</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                  {errors.department && <p className="text-brand-red text-sm mt-1">{errors.department}</p>}
                </div>
              </div>

              {/* Nível */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nível *
                </label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <select
                    value={formData.basicInfo.level}
                    onChange={(e) => handleInputChange('basicInfo', 'level', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white focus:border-brand-primary focus:outline-none ${errors.level ? 'border-brand-red' : 'border-gray-700'}`}
                  >
                    <option value="">Selecione o nível</option>
                    {levels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                  {errors.level && <p className="text-brand-red text-sm mt-1">{errors.level}</p>}
                </div>
              </div>

              {/* Tipo de Contrato */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tipo de Contrato
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="contractType"
                      value="CLT"
                      checked={formData.basicInfo.contractType === 'CLT'}
                      onChange={(e) => handleInputChange('basicInfo', 'contractType', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-2 ${formData.basicInfo.contractType === 'CLT' ? 'border-brand-primary bg-brand-primary' : 'border-gray-400'}`}>
                      {formData.basicInfo.contractType === 'CLT' && (
                        <div className="w-2 h-2 bg-brand-dark rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className="text-white">CLT</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="contractType"
                      value="PJ"
                      checked={formData.basicInfo.contractType === 'PJ'}
                      onChange={(e) => handleInputChange('basicInfo', 'contractType', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-2 ${formData.basicInfo.contractType === 'PJ' ? 'border-brand-primary bg-brand-primary' : 'border-gray-400'}`}>
                      {formData.basicInfo.contractType === 'PJ' && (
                        <div className="w-2 h-2 bg-brand-dark rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className="text-white">PJ</span>
                  </label>
                </div>
              </div>

              {/* Localização */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Localização
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={formData.basicInfo.location}
                    onChange={(e) => handleInputChange('basicInfo', 'location', e.target.value)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                    placeholder="Brasília/DF"
                  />
                </div>
              </div>
            </div>

            {/* Modalidade de Trabalho */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-4">
                Modalidade de Trabalho
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { value: 'remote', label: 'Remoto', description: '100% trabalho remoto' },
                  { value: 'hybrid', label: 'Híbrido', description: 'Flexibilidade entre remoto e presencial' },
                  { value: 'onsite', label: 'Presencial', description: 'Trabalho no escritório' }
                ].map(option => (
                  <label key={option.value} className="cursor-pointer">
                    <input
                      type="radio"
                      name="workMode"
                      value={option.value}
                      checked={formData.basicInfo.workMode === option.value}
                      onChange={(e) => handleInputChange('basicInfo', 'workMode', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`p-4 border-2 rounded-monumenta transition-colors ${
                      formData.basicInfo.workMode === option.value 
                        ? 'border-brand-primary bg-brand-primary/10' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}>
                      <h3 className={`font-medium ${formData.basicInfo.workMode === option.value ? 'text-brand-primary' : 'text-white'}`}>
                        {option.label}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Urgência da Contratação */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-4">
                Urgência da Contratação
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: 'low', label: 'Baixa', color: 'text-blue-200 border-blue-200' },
                  { value: 'normal', label: 'Normal', color: 'text-brand-primary border-brand-primary' },
                  { value: 'high', label: 'Alta', color: 'text-yellow-200 border-yellow-200' },
                  { value: 'urgent', label: 'Urgente', color: 'text-red-200 border-red-200' }
                ].map(option => (
                  <label key={option.value} className="cursor-pointer">
                    <input
                      type="radio"
                      name="urgency"
                      value={option.value}
                      checked={formData.basicInfo.urgency === option.value}
                      onChange={(e) => handleInputChange('basicInfo', 'urgency', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`p-3 border-2 rounded-monumenta text-center transition-colors ${
                      formData.basicInfo.urgency === option.value 
                        ? `${option.color} bg-current bg-opacity-10` 
                        : 'border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}>
                      <span className="font-medium">{option.label}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Faixa Salarial */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Salário Mínimo (R$) *
                </label>
                <input
                  type="number"
                  value={formData.compensation.salaryMin}
                  onChange={(e) => handleInputChange('compensation', 'salaryMin', parseFloat(e.target.value) || 0)}
                  className={`w-full bg-brand-dark border rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none ${errors.salaryMin ? 'border-brand-red' : 'border-gray-700'}`}
                  placeholder="5000"
                  min="0"
                  step="100"
                />
                {errors.salaryMin && <p className="text-brand-red text-sm mt-1">{errors.salaryMin}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Salário Máximo (R$) *
                </label>
                <input
                  type="number"
                  value={formData.compensation.salaryMax}
                  onChange={(e) => handleInputChange('compensation', 'salaryMax', parseFloat(e.target.value) || 0)}
                  className={`w-full bg-brand-dark border rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none ${errors.salaryMax ? 'border-brand-red' : 'border-gray-700'}`}
                  placeholder="8000"
                  min="0"
                  step="100"
                />
                {errors.salaryMax && <p className="text-brand-red text-sm mt-1">{errors.salaryMax}</p>}
              </div>

              {/* Mostrar Salário */}
              <div className="md:col-span-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.compensation.showSalary}
                    onChange={(e) => handleInputChange('compensation', 'showSalary', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 border-2 border-gray-400 rounded peer-checked:border-brand-primary peer-checked:bg-brand-primary relative">
                    {formData.compensation.showSalary && (
                      <CheckCircle className="absolute -top-0.5 -left-0.5 text-brand-primary" size={20} />
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-white font-medium">Mostrar faixa salarial na vaga</span>
                    <p className="text-sm text-gray-400">Torna a vaga mais atrativa e transparente</p>
                  </div>
                </label>
              </div>

              {/* Carga Horária */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Carga Horária Semanal (horas)
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="number"
                    value={formData.compensation.workload}
                    onChange={(e) => handleInputChange('compensation', 'workload', parseInt(e.target.value) || 40)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                    min="1"
                    max="44"
                  />
                </div>
              </div>
            </div>

            {/* Benefícios */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Benefícios Oferecidos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {defaultBenefits.map(benefit => (
                  <label key={benefit} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.compensation.benefits.includes(benefit)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            compensation: {
                              ...prev.compensation,
                              benefits: [...prev.compensation.benefits, benefit]
                            }
                          }))
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            compensation: {
                              ...prev.compensation,
                              benefits: prev.compensation.benefits.filter(b => b !== benefit)
                            }
                          }))
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 border-2 border-gray-400 rounded peer-checked:border-brand-primary peer-checked:bg-brand-primary relative">
                      {formData.compensation.benefits.includes(benefit) && (
                        <CheckCircle className="absolute -top-0.5 -left-0.5 text-brand-primary" size={20} />
                      )}
                    </div>
                    <span className="ml-3 text-white">{benefit}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Preview da Faixa Salarial */}
            {formData.compensation.salaryMin > 0 && formData.compensation.salaryMax > 0 && (
              <div className="bg-brand-dark-light p-6 rounded-monumenta">
                <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                  Preview da Remuneração
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Faixa Salarial:</span>
                    <span className="text-white font-medium">
                      {formData.compensation.showSalary 
                        ? `R$ ${formData.compensation.salaryMin.toLocaleString('pt-BR')} - R$ ${formData.compensation.salaryMax.toLocaleString('pt-BR')}`
                        : 'A combinar'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Carga Horária:</span>
                    <span className="text-white font-medium">{formData.compensation.workload}h semanais</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Benefícios:</span>
                    <span className="text-brand-primary">
                      {formData.compensation.benefits.length} selecionados
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            {/* Resumo da Vaga */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Resumo da Vaga *
              </label>
              <textarea
                value={formData.description.summary}
                onChange={(e) => handleInputChange('description', 'summary', e.target.value)}
                rows={4}
                className={`w-full bg-brand-dark border rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none resize-none ${errors.summary ? 'border-brand-red' : 'border-gray-700'}`}
                placeholder="Descreva em poucas palavras o que a pessoa fará nesta vaga e qual o impacto do seu trabalho..."
              />
              {errors.summary && <p className="text-brand-red text-sm mt-1">{errors.summary}</p>}
              <p className="text-xs text-gray-400 mt-1">
                {formData.description.summary.length}/500 caracteres
              </p>
            </div>

            {/* Responsabilidades */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Principais Responsabilidades *
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('description', 'responsibilities', value)}
                  placeholder="Nova responsabilidade..."
                />
              </div>
              
              {formData.description.responsibilities.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhuma responsabilidade adicionada</p>
              ) : (
                <div className="space-y-2">
                  {formData.description.responsibilities.map((responsibility, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{responsibility}</span>
                      <button
                        onClick={() => removeFromArray('description', 'responsibilities', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {errors.responsibilities && <p className="text-brand-red text-sm mt-2">{errors.responsibilities}</p>}
            </div>

            {/* Requisitos */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Requisitos Obrigatórios *
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('description', 'requirements', value)}
                  placeholder="Novo requisito..."
                />
              </div>
              
              {formData.description.requirements.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum requisito adicionado</p>
              ) : (
                <div className="space-y-2">
                  {formData.description.requirements.map((requirement, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{requirement}</span>
                      <button
                        onClick={() => removeFromArray('description', 'requirements', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {errors.requirements && <p className="text-brand-red text-sm mt-2">{errors.requirements}</p>}
            </div>

            {/* Requisitos Preferenciais */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Requisitos Preferenciais
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('description', 'preferences', value)}
                  placeholder="Requisito preferencial..."
                />
              </div>
              
              {formData.description.preferences.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum requisito preferencial adicionado</p>
              ) : (
                <div className="space-y-2">
                  {formData.description.preferences.map((preference, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{preference}</span>
                      <button
                        onClick={() => removeFromArray('description', 'preferences', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desafios da Posição */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Principais Desafios
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('description', 'challenges', value)}
                  placeholder="Novo desafio..."
                />
              </div>
              
              {formData.description.challenges.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum desafio adicionado</p>
              ) : (
                <div className="space-y-2">
                  {formData.description.challenges.map((challenge, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{challenge}</span>
                      <button
                        onClick={() => removeFromArray('description', 'challenges', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            {/* Descrição do Time */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descrição do Time
              </label>
              <textarea
                value={formData.culture.teamDescription}
                onChange={(e) => handleInputChange('culture', 'teamDescription', e.target.value)}
                rows={4}
                className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none resize-none"
                placeholder="Descreva o time que a pessoa vai integrar, como trabalham, projetos que desenvolvem..."
              />
            </div>

            {/* Oportunidades de Crescimento */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Oportunidades de Crescimento
              </label>
              <textarea
                value={formData.culture.growthOpportunities}
                onChange={(e) => handleInputChange('culture', 'growthOpportunities', e.target.value)}
                rows={4}
                className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none resize-none"
                placeholder="Quais são as possibilidades de desenvolvimento profissional nesta posição..."
              />
            </div>

            {/* Valores da Empresa */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Valores da Monumenta
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {monumentaValues.map(value => (
                  <label key={value} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.culture.companyValues.includes(value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            culture: {
                              ...prev.culture,
                              companyValues: [...prev.culture.companyValues, value]
                            }
                          }))
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            culture: {
                              ...prev.culture,
                              companyValues: prev.culture.companyValues.filter(v => v !== value)
                            }
                          }))
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 border-2 border-gray-400 rounded peer-checked:border-brand-primary peer-checked:bg-brand-primary relative">
                      {formData.culture.companyValues.includes(value) && (
                        <CheckCircle className="absolute -top-0.5 -left-0.5 text-brand-primary" size={20} />
                      )}
                    </div>
                    <span className="ml-3 text-white">{value}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Ambiente de Trabalho */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ambiente de Trabalho
              </label>
              <textarea
                value={formData.culture.workEnvironment}
                onChange={(e) => handleInputChange('culture', 'workEnvironment', e.target.value)}
                rows={4}
                className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none resize-none"
                placeholder="Descreva como é o dia a dia na Monumenta, a cultura, o ambiente..."
              />
            </div>

            {/* Preview da Cultura */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Preview - Por que trabalhar na Monumenta?
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-white mb-2">Nossa Missão</h4>
                  <p className="text-gray-300 text-sm">
                    A Monu faz história DE UM JEITO SÓ DELA: ORGULHOSAMENTE INDEPENDENTE desde 1995, 
                    criando experiências de marca significativas que harmonizam performance com o ritmo do novo consumidor.
                  </p>
                </div>
                
                {formData.culture.companyValues.length > 0 && (
                  <div>
                    <h4 className="font-medium text-white mb-2">Nossos Valores</h4>
                    <div className="flex flex-wrap gap-2">
                      {formData.culture.companyValues.map(value => (
                        <span key={value} className="px-3 py-1 bg-brand-primary/20 text-brand-primary rounded-monumenta text-sm">
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-white mb-2">Oportunidades</h4>
                  <p className="text-gray-300 text-sm">
                    {formData.culture.growthOpportunities || 'Crescimento profissional em uma agência independente e inovadora.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )

      case 5:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Gestor Responsável */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Gestor Responsável *
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <select
                    value={formData.process.hiringManager}
                    onChange={(e) => handleInputChange('process', 'hiringManager', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white focus:border-brand-primary focus:outline-none ${errors.hiringManager ? 'border-brand-red' : 'border-gray-700'}`}
                  >
                    <option value="">Selecione o gestor</option>
                    {managers.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name} - {manager.department}
                      </option>
                    ))}
                  </select>
                  {errors.hiringManager && <p className="text-brand-red text-sm mt-1">{errors.hiringManager}</p>}
                </div>
              </div>

              {/* Data de Início Esperada */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Data de Início Esperada *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    value={formData.process.expectedStartDate}
                    onChange={(e) => handleInputChange('process', 'expectedStartDate', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none ${errors.expectedStartDate ? 'border-brand-red' : 'border-gray-700'}`}
                  />
                  {errors.expectedStartDate && <p className="text-brand-red text-sm mt-1">{errors.expectedStartDate}</p>}
                </div>
              </div>

              {/* Vagas Disponíveis */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Número de Vagas
                </label>
                <input
                  type="number"
                  value={formData.process.positionsAvailable}
                  onChange={(e) => handleInputChange('process', 'positionsAvailable', parseInt(e.target.value) || 1)}
                  className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                  min="1"
                  max="10"
                />
              </div>

              {/* Prazo para Candidaturas */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Prazo para Candidaturas
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    value={formData.process.applicationDeadline}
                    onChange={(e) => handleInputChange('process', 'applicationDeadline', e.target.value)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Etapas do Processo */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Etapas do Processo Seletivo
                </h3>
                <button
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      process: {
                        ...prev.process,
                        stages: defaultProcessStages
                      }
                    }))
                  }}
                  className="text-sm text-brand-primary hover:text-brand-primary/80"
                >
                  Usar processo padrão
                </button>
              </div>

              {formData.process.stages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 mb-4">Nenhuma etapa definida</p>
                  <button
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        process: {
                          ...prev.process,
                          stages: defaultProcessStages
                        }
                      }))
                    }}
                    className="px-4 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
                  >
                    Adicionar Processo Padrão
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.process.stages.map((stage, index) => (
                    <div key={index} className="bg-brand-dark p-4 rounded-monumenta">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="bg-brand-primary text-brand-dark rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">
                              {index + 1}
                            </span>
                            <h4 className="font-medium text-white">{stage.name}</h4>
                            <span className="ml-auto text-sm text-brand-primary">{stage.duration}</span>
                          </div>
                          <p className="text-gray-400 text-sm ml-9">{stage.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Timeline Preview */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Timeline Estimado do Processo
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">Início das candidaturas:</span>
                  <span className="text-white font-medium">Imediatamente após publicação</span>
                </div>
                {formData.process.applicationDeadline && (
                  <div className="flex justify-between">
                    <span className="text-gray-300">Encerramento das candidaturas:</span>
                    <span className="text-white font-medium">
                      {new Date(formData.process.applicationDeadline).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
                {formData.process.expectedStartDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-300">Início esperado:</span>
                    <span className="text-brand-primary font-medium">
                      {new Date(formData.process.expectedStartDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-300">Duração estimada:</span>
                  <span className="text-white font-medium">
                    {formData.process.stages.length > 0 ? '3-4 semanas' : 'A definir'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )

      case 6:
        return (
          <div className="space-y-6">
            {/* Preview da Vaga */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-display font-bold text-white mb-2">
                    {formData.basicInfo.title}
                  </h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <span className="flex items-center">
                      <Building size={16} className="mr-1" />
                      {formData.basicInfo.department}
                    </span>
                    <span className="flex items-center">
                      <MapPin size={16} className="mr-1" />
                      {formData.basicInfo.location}
                    </span>
                    <span className="flex items-center">
                      <Clock size={16} className="mr-1" />
                      {formData.basicInfo.workMode === 'remote' ? 'Remoto' : formData.basicInfo.workMode === 'hybrid' ? 'Híbrido' : 'Presencial'}
                    </span>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-monumenta text-sm font-medium ${
                  formData.basicInfo.urgency === 'urgent' ? 'bg-red-900/50 text-red-200 border border-red-200' :
                  formData.basicInfo.urgency === 'high' ? 'bg-yellow-900/50 text-yellow-200 border border-yellow-200' :
                  formData.basicInfo.urgency === 'normal' ? 'bg-brand-primary/20 text-brand-primary border border-brand-primary' :
                  'bg-blue-900/50 text-blue-200 border border-blue-200'
                }`}>
                  {formData.basicInfo.urgency === 'urgent' ? 'Urgente' :
                   formData.basicInfo.urgency === 'high' ? 'Alta' :
                   formData.basicInfo.urgency === 'normal' ? 'Normal' : 'Baixa'}
                </div>
              </div>

              {formData.compensation.showSalary && (
                <div className="mb-4">
                  <span className="text-brand-primary font-medium text-lg">
                    R$ {formData.compensation.salaryMin.toLocaleString('pt-BR')} - R$ {formData.compensation.salaryMax.toLocaleString('pt-BR')}
                  </span>
                  <span className="text-gray-400 ml-2">• {formData.compensation.workload}h semanais • {formData.basicInfo.contractType}</span>
                </div>
              )}

              <p className="text-gray-300 mb-6">{formData.description.summary}</p>

              {/* Responsabilidades */}
              {formData.description.responsibilities.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-display font-medium text-brand-primary mb-3">O que você vai fazer:</h3>
                  <ul className="space-y-2">
                    {formData.description.responsibilities.map((responsibility, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-brand-primary mr-2">•</span>
                        <span className="text-gray-300">{responsibility}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Requisitos */}
              {formData.description.requirements.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-display font-medium text-brand-primary mb-3">Requisitos obrigatórios:</h3>
                  <ul className="space-y-2">
                    {formData.description.requirements.map((requirement, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle size={16} className="text-brand-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Benefícios */}
              {formData.compensation.benefits.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-display font-medium text-brand-primary mb-3">Benefícios:</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {formData.compensation.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center">
                        <CheckCircle size={16} className="text-brand-primary mr-2" />
                        <span className="text-gray-300 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {formData.process.positionsAvailable}
                </div>
                <div className="text-sm text-gray-400">
                  {formData.process.positionsAvailable === 1 ? 'Vaga disponível' : 'Vagas disponíveis'}
                </div>
              </div>

              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {formData.process.stages.length}
                </div>
                <div className="text-sm text-gray-400">Etapas do processo</div>
              </div>

              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {formData.compensation.benefits.length}
                </div>
                <div className="text-sm text-gray-400">Benefícios inclusos</div>
              </div>
            </div>

            {/* Confirmação */}
            <div className="bg-green-900/20 border border-green-500/50 rounded-monumenta p-4">
              <div className="flex">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h4 className="text-green-500 font-medium mb-1">Vaga pronta para publicação</h4>
                  <p className="text-green-100 text-sm">
                    Todos os campos obrigatórios foram preenchidos. A vaga será publicada imediatamente 
                    e os candidatos poderão se inscrever através da plataforma.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  // Component for adding items to arrays
  const AddItemButton = ({ onAdd, placeholder }: { onAdd: (value: string) => void; placeholder: string }) => {
    const [inputValue, setInputValue] = useState('')
    const [isOpen, setIsOpen] = useState(false)

    const handleAdd = () => {
      if (inputValue.trim()) {
        onAdd(inputValue)
        setInputValue('')
        setIsOpen(false)
      }
    }

    if (!isOpen) {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center px-4 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
        >
          <Plus size={16} className="mr-2" />
          Adicionar
        </button>
      )
    }

    return (
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          className="px-3 py-2 bg-brand-dark border border-gray-700 rounded-monumenta text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          autoFocus
        />
        <button
          onClick={handleAdd}
          className="px-3 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
        >
          <CheckCircle size={16} />
        </button>
        <button
          onClick={() => {
            setInputValue('')
            setIsOpen(false)
          }}
          className="px-3 py-2 text-gray-400 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    )
  }

  return (
    
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="mb-8">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => window.location.href = '/recruitment'}
                    className="flex items-center px-4 py-2 text-gray-400 hover:text-brand-primary transition-colors"
                  >
                    <ArrowLeft size={20} className="mr-2" />
                    Voltar
                  </button>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center space-x-4 mb-8 overflow-x-auto">
                  {steps.map((step, index) => {
                    const Icon = step.icon
                    const isActive = currentStep === step.id
                    const isCompleted = currentStep > step.id
                    
                    return (
                      <div key={step.id} className="flex items-center flex-shrink-0">
                        <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors
                    ${isActive 
                            ? 'border-brand-primary bg-brand-primary text-brand-dark' 
                            : isCompleted 
                              ? 'border-brand-primary bg-brand-primary text-brand-dark'
                              : 'border-gray-600 text-gray-400'
                          }
                  `}>
                          {isCompleted ? (
                            <CheckCircle size={20} />
                          ) : (
                            <Icon size={20} />
                          )}
                        </div>
                        <div className="ml-3 hidden sm:block">
                          <p className={`text-sm font-medium whitespace-nowrap ${isActive ? 'text-brand-primary' : isCompleted ? 'text-brand-primary' : 'text-gray-400'}`}>
                            {step.title}
                          </p>
                        </div>
                        {index < steps.length - 1 && (
                          <div className={`hidden sm:block w-8 h-0.5 ml-4 ${isCompleted ? 'bg-brand-primary' : 'bg-gray-600'}`} />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Form Content */}
              <div className="bg-brand-dark-light rounded-monumenta p-8">
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
                  <button
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    className="flex items-center px-6 py-3 bg-gray-700 text-white rounded-monumenta hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft size={20} className="mr-2" />
                    Anterior
                  </button>

                  {currentStep < steps.length ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center px-6 py-3 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors font-medium"
                    >
                      Próximo
                      <ArrowRight size={20} className="ml-2" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex items-center px-6 py-3 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors font-medium disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-dark mr-2"></div>
                          Publicando...
                        </>
                      ) : (
                        <>
                          <Send size={20} className="mr-2" />
                          Publicar Vaga
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          
  )
}

// Import X component that was used but not imported
const X = ({ size, className }: { size: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m18 6-12 12" />
    <path d="m6 6 12 12" />
  </svg>
) 